<template>
    <!-- Edit Times for Network Maintenance -->
    <div    class="flex justify-center w-full">
        <div class="flex flex-col w-1/3 items-center">
            <h2>Network Maintenance Mode</h2>
            <p>
                Enabling Network Maintenance Mode will suppress offline alarm notifications for all points of monitoring
                within the time window chosen. It will also show a banner on the site informing users of the network
                maintenance status.
            </p>

            <!-- Start Time -->
            <div    class="w-full">
                <label  for="start_time">Start Time</label><span   v-if="startTime === null">: Currently not Set</span>
                <date-time-picker   id="start_time"
                                    v-model="$data._startTime"
                                    class="w-full"
                                    :min-date="currentDate"
                                    :side-by-side="true"
                                    :timezone="timezone"
                                    format="MMMM DD, YYYY HH:mm"
                                    vertical-position="bottom"
                ></date-time-picker>
            </div>

            <!-- End Time -->
            <div    class="w-full pt-2 pb-2">
                <label  for="end_time">End Time</label><span   v-if="endTime === null">: Currently not Set</span>
                <date-time-picker   id="end_time"
                                    v-model="$data._endTime"
                                    :min-date="$data._startTime"
                                    :side-by-side="true"
                                    :timezone="timezone"
                                    format="MMMM DD, YYYY HH:mm"
                                    vertical-position="top"
                ></date-time-picker>
            </div>

            <!-- Select2 of Parent Company -->
            <div    v-if="isEnterpriseNetworkMaintenance"
                    class="w-full pb-4 pt-2">
                <select2    v-model:value="$data._companyId"
                            :multiple="false"
                            :options="companies"
                            name="Parent Company"
                ></select2>
            </div>

            <!-- Submit Button -->
            <div    class="w-4/5">
                <button-blue-primary    @click="setNetworkMaintenanceTimes">
                    Set Network Maintenance Times
                </button-blue-primary>
            </div>

            <!-- Banners -->
            <div    class="w-full">
                <!-- Successful updated message -->
                <banner-success     v-if="successMessage">
                    <template    v-slot:title>Success</template>
                    <template    v-slot:content>
                        <p>{{successMessage}}</p>
                    </template>
                </banner-success>

                <banner-error   v-if="errorMessage">
                    <template    v-slot:title>Error</template>
                    <template    v-slot:content>
                        <p>{{ errorMessage }}</p>
                    </template>
                </banner-error>
            </div>

            <!-- Clear Times for Network Maintenance -->
            <h2>Clear Times</h2>
            <p>
                Clear the time interval for network maintenance.
            </p>

            <div    class="w-4/5">
                <button-red-primary     @click="clearNetworkMaintenanceTimes">
                    Clear Network Maintenance Times
                </button-red-primary>
            </div>

            <!-- Successful clear message -->
            <div    class="w-full">
                <banner-success     v-if="successClearMessage">
                    <template    v-slot:title>Success</template>
                    <template    v-slot:content>
                        <p>{{successClearMessage}}</p>
                    </template>
                </banner-success>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import ButtonRedPrimary from "../elements/buttons/ButtonRedPrimary.vue";
import ButtonBluePrimary from "../elements/buttons/ButtonBluePrimary.vue";
import DateTimePicker from "../Reports/VueDatePicker.vue";
import BannerSuccess from "../Banners/BannerSuccess.vue";
import BannerError from "../Banners/BannerError.vue";
import Select2 from "../elements/select2.vue";

export default {
    name: "NetworkMaintenanceMode",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {Select2, BannerError, BannerSuccess, DateTimePicker, ButtonBluePrimary, ButtonRedPrimary},
    emits: [],
    props: {
        // Props
        timezone: {
            type: String,
            required: true
        },
        startTime: {
            type: String,
            required: true
        },
        endTime: {
            type: String,
            required: true
        },
        companies: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            // Data Properties
            currentDate: new Date(),
            _startTime: this.startTime,
            _endTime: this.endTime,
            _companyId: null,

            // Banner Messages
            successMessage: null,
            successClearMessage: null,
            errorMessage: null,
        }
    },
    created() {
        // DateTimePicker no longer auto-fills empty inputs at the wrapper level
        // (it would collapse range pickers to a zero-width window), so seed
        // sensible defaults here when no maintenance window is configured.
        if (! this._startTime) {
            this._startTime = new Date()
        }
        if (! this._endTime) {
            this._endTime = new Date(Date.now() + 60 * 60 * 1000)
        }
    },
    mounted() {
        this._companyId = this.company.getCompanyId()
    },
    methods: {
        setNetworkMaintenanceTimes() {
            this.clearBannerMessages()
            axios.patch(this.company.buildCompanyApiInternalRoute('network-maintenance'), {
                isEnterpriseNetworkMaintenance: this.isEnterpriseNetworkMaintenance,
                companyId: this.$data._companyId,
                startTime: this.$data._startTime,
                endTime: this.$data._endTime
            }).then(response => {
                this.successMessage = response.data.message
            }).catch(error => {
                this.errorMessage = error.response.data
            }).then(() => {
                this.clearBannerAfterDelay()
            })
        },
        clearNetworkMaintenanceTimes() {
            this.clearBannerMessages()
            axios.delete(this.company.buildCompanyApiInternalRoute('network-maintenance'), {
                params: {
                    isEnterpriseNetworkMaintenance: this.isEnterpriseNetworkMaintenance,
                    companyId: this.$data._companyId,
                },
            }).then(response => {
                this.successClearMessage = response.data.message
            }).catch(error => {
                this.errorMessage = error.response.data
            }).then(() => {
                this.clearBannerAfterDelay()
            })
        },
        clearBannerMessages() {
            this.successMessage = null
            this.successClearMessage = false
            this.errorMessage = null
        },
        clearBannerAfterDelay() {
            // Clear the banner after 10 seconds
            setTimeout(() => {
                this.refresh()
            }, 10000)
        },
        refresh() {
            window.location.reload()
        }
    },
    computed: {
        isEnterpriseNetworkMaintenance() {
            return Object.keys(this.companies).length > 1
        },
    },
    watch: {},
}
</script>

<style scoped>

</style>